import { createContext, useContext, type ReactNode } from "react";
import { Check, Circle, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Barra de pestañas del estándar del Portal de Cobranza: subrayado en la activa,
 * ícono chico y contador opcional.
 *
 * Estaba escrita a mano dentro de las pantallas de cobranza (`CobranzaAtencion`).
 * Vive aquí para que cualquier portal la reuse sin volver a copiar clases -igual
 * que se hizo con `KpiCard` y `DataTable`.
 */
/**
 * Pestañas planas: solo texto, sin ícono ni tooltip. Lo activa un portal completo desde su layout
 * (Portal de Cobranza, Eduardo 2026-10-03: "solo el texto es suficiente, los tooltips son ruido"),
 * así cada pantalla y cada sheet del portal sigue la misma regla sin tocar sus definiciones, y los
 * demás portales quedan igual. Atraviesa los portales de React (sheets y modales).
 */
const TabBarPlanoContext = createContext(false);

export function TabBarPlano({ children }: { children: ReactNode }) {
  return <TabBarPlanoContext.Provider value={true}>{children}</TabBarPlanoContext.Provider>;
}

export interface TabBarItem<T extends string = string> {
  id: T;
  label: string;
  icon?: LucideIcon;
  /** Se pinta como chip a la derecha de la etiqueta. Se oculta si es 0. */
  count?: number;
  /**
   * Qué hay en la pestaña, para quien la usa. Opcional: si viene, la explicación sale en un
   * Tooltip sobre toda la pestaña (al pasar el mouse o al llegar con teclado), sin icono extra y
   * en vez de una franja de texto fija debajo de la barra. Ancho y alto acotados, con scroll vertical
   * si el texto no cabe.
   */
  descripcion?: string;
  /**
   * Marca de recorrido (oferta digital, 2026-10-23): "visitada" pinta una palomita y "pendiente" un
   * círculo vacío al final de la pestaña. Sin valor no se pinta nada.
   */
  estado?: "visitada" | "pendiente";
}

export interface TabBarProps<T extends string = string> {
  tabs: TabBarItem<T>[];
  value: T;
  /**
   * Recibe el id de la pestaña. Pasar `setEstado` directo NO compila: un `Dispatch<SetStateAction<T>>`
   * acepta también la forma de función, así que TypeScript deja de inferir `T` y cae a `string`.
   * Va envuelto: `onChange={(v) => setEstado(v)}`.
   */
  onChange: (id: T) => void;
  className?: string;
  /**
   * "sm" (default) = el compacto de siempre, sin tocar a quien ya lo usa así.
   * "md" = la densidad de las pantallas del portal (px-4 py-2.5, texto 13px).
   * "lg" = texto base, para encabezados de sección.
   *
   * Ninguna reparte el ancho ni pinta fondo en la activa: el subrayado basta y
   * las pestañas se quedan del tamaño de su contenido.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Debajo de `md`: "scroll" (default) = la misma fila con scroll lateral; "cuadricula" = 2
   * columnas con el nombre completo en varias líneas y botones de 44 px o más, para páginas
   * públicas donde los nombres no caben (oferta digital). Desde `md` es la barra de siempre.
   */
  movil?: "scroll" | "cuadricula";
  /**
   * "subrayado" (default) = la barra de siempre. "segmentado" = pista gris con la activa en blanco
   * y borde (las pestañas del módulo de Precios y de Reventas, 2026-10-26): para secciones de una
   * misma pantalla de trabajo donde el subrayado se perdía entre tarjetas. Mismo scroll lateral
   * oculto en móvil; ignora `movil`.
   */
  variante?: "subrayado" | "segmentado";
}

const SIZE_CLASS: Record<"sm" | "md" | "lg", string> = {
  sm: "px-3 py-2 text-[12px]",
  md: "px-4 py-2.5 text-[13px]",
  lg: "px-4 py-2.5 text-base",
};

// Mismas medidas desde `md`, para la variante en cuadrícula (Tailwind necesita las clases enteras).
const SIZE_CLASS_DESDE_MD: Record<"sm" | "md" | "lg", string> = {
  sm: "md:px-3 md:py-2 md:text-[12px]",
  md: "md:px-4 md:py-2.5 md:text-[13px]",
  lg: "md:px-4 md:py-2.5 md:text-base",
};

export function TabBar<T extends string = string>({ tabs, value, onChange, className, size = "sm", movil = "scroll", variante = "subrayado" }: TabBarProps<T>) {
  const plano = useContext(TabBarPlanoContext);
  if (variante === "segmentado") {
    return (
      <div
        role="tablist"
        className={cn(
          "inline-flex max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden rounded-lg bg-muted p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
      >
        {tabs.map((tab) => {
          const active = value === tab.id;
          const Icon = plano ? undefined : tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.id)}
              className={cn(
                "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-[13px] transition-colors",
                "outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30",
                active
                  ? "border-border bg-background font-medium text-foreground shadow-sm"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {Icon && <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />}
              <span>{tab.label}</span>
              {tab.count != null && <span className="tabular-nums">({tab.count.toLocaleString("es-MX")})</span>}
            </button>
          );
        })}
      </div>
    );
  }
  const cuadricula = movil === "cuadricula";
  return (
    <div
      role="tablist"
      className={cn(
        // overflow-y-hidden: el scroll horizontal no debe abrir barra vertical. La barra de scroll se
        // oculta: el subrayado ya marca la pestaña y se sigue desplazando con trackpad o al tocar.
        cuadricula
          ? "grid grid-cols-2 gap-2 md:flex md:items-center md:gap-0 md:border-b md:border-border md:overflow-x-auto md:overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          : "flex items-center border-b border-border overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {tabs.map((tab) => {
        const active = value === tab.id;
        const Icon = plano ? undefined : tab.icon;
        const boton = (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 font-medium transition-colors",
              // Sin el recuadro negro del navegador al hacer clic; con teclado, un anillo sutil por dentro.
              "outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30",
              cuadricula
                ? cn(
                    // Móvil: tarjeta de 44 px con el nombre completo; la activa solo cambia borde y texto.
                    "min-h-[44px] rounded-lg border bg-card px-3 py-2 text-left text-sm leading-tight",
                    "md:min-h-0 md:rounded-none md:border-0 md:border-b-2 md:-mb-px md:bg-transparent md:whitespace-nowrap",
                    SIZE_CLASS_DESDE_MD[size],
                    active
                      ? "border-primary text-primary"
                      : "border-border text-muted-foreground hover:text-foreground md:border-transparent",
                  )
                : cn(
                    "border-b-2 -mb-px whitespace-nowrap",
                    SIZE_CLASS[size],
                    active
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  ),
            )}
          >
            {Icon && <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />}
            <span className={cuadricula ? "min-w-0 flex-1 md:flex-none" : undefined}>{tab.label}</span>
            {tab.count != null && tab.count > 0 && (
              <span className="text-[10px] px-1 rounded-full min-w-[16px] text-center bg-muted text-muted-foreground">
                {tab.count.toLocaleString("es-MX")}
              </span>
            )}
            {tab.estado === "visitada" && <Check className="w-3.5 h-3.5 shrink-0 text-primary" aria-label="Visitada" />}
            {tab.estado === "pendiente" && <Circle className="w-3 h-3 shrink-0 text-muted-foreground/50" aria-label="Pendiente" />}
          </button>
        );
        if (plano || !tab.descripcion) return boton;
        return (
          <Tooltip key={tab.id} delayDuration={200}>
            <TooltipTrigger asChild>{boton}</TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="start"
              className="max-w-xs max-h-48 overflow-x-hidden overflow-y-auto text-[12px] leading-snug whitespace-normal"
            >
              {tab.descripcion}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
